import { ContentItem, UserContentDto } from "@coaching-culture/types";
import { sortBy } from "lodash";
import {
  EmptyState,
  Flex,
  Grid,
  IconButton,
  Panel,
} from "@coaching-culture/ui";
import { ContentCard } from "components/ContentCard";
import ShareModal, { ShareConfig } from "components/ShareModal";
import React, { useState } from "react";
import { FaBookOpen, FaList, FaTh } from "react-icons/fa";
import { useHistory } from "react-router";
import { fromUnixTime } from "date-fns";

const getLastAttempt = (content: UserContentDto) =>
  content.attempts
    .map((x) => fromUnixTime(x.startedOn))
    .sort()
    .reverse()[0];

const getActiveAttempt = (content: UserContentDto) =>
  content.attempts.find((x) => x.completedOn == null);

type UserContentDisplayProps = {
  content: UserContentDto;
  onShare: () => void;
  onSelect: () => void;
  displayMode: "list" | "grid";
};

const UserContentDisplay = ({
  content,
  onSelect,
  onShare,
  displayMode,
}: UserContentDisplayProps) => {
  const { name, description, icon, attempts } = content;

  const lastAttempt = getLastAttempt(content);
  const activeAttempt = getActiveAttempt(content);
  const completed = attempts
    .filter((x) => x.completedOn != null)
    .map((x) => x.completedOn)
    .sort()[0];

  return (
    <ContentCard
      accessReasons={content.assignmentReasons}
      image={icon}
      onSelect={onSelect}
      onShare={onShare}
      title={name}
      activeAttempt={activeAttempt != null}
      canShare={content.canShare}
      completed={completed != null ? fromUnixTime(completed) : null}
      description={description}
      lastAttempt={lastAttempt}
      displayMode={displayMode}
    />
  );
};

const getContentUrl = (content: ContentItem) => {
  const type =
    content.type === "module"
      ? "mindset"
      : content.type === "lesson"
        ? "lessons"
        : content.type === "learncast"
          ? "learncast"
          : "assessments";

  return `/solutions/${type}/modules/${content.slug}`;
};

export default function ContentList({ items }: { items: UserContentDto[] }) {
  const [displayMode, setDisplayMode] = useState<"list" | "grid">("grid");
  const [sharingModule, setSharingModule] = useState<ShareConfig | null>(null);
  const history = useHistory();

  const gotoContent = (content: UserContentDto) => {
    history.push(getContentUrl(content));
  };

  const shareContent = (content: UserContentDto) => {
    setSharingModule({
      type: content.type,
      itemId: content.id,
      targetId: null,
      targetType: null,
    });
  };

  const isEmpty = (items ?? []).length === 0;
  const modules = sortBy(items, "orderIndex").map((x) => (
    <UserContentDisplay
      content={x}
      key={x.id}
      onSelect={() => gotoContent(x)}
      onShare={() => shareContent(x)}
      displayMode={displayMode}
    />
  ));

  return (
    <>
      {sharingModule && (
        <ShareModal
          config={sharingModule}
          onClose={() => setSharingModule(null)}
        />
      )}
      <Flex justifyContent="flex-end" mb={3}>
        <IconButton
          icon={FaList}
          color={displayMode === "list" ? "black" : "grey"}
          onClick={() => setDisplayMode("list")}
          aria-label="Show as list"
          aria-pressed={displayMode === "list"}
          style={{ fontSize: "1.3em" }} // design-lint-ignore: icon sizing in em
        />
        <IconButton
          icon={FaTh}
          color={displayMode === "grid" ? "black" : "grey"}
          onClick={() => setDisplayMode("grid")}
          aria-label="Show as grid"
          aria-pressed={displayMode === "grid"}
          style={{ fontSize: "1.3em" }} // design-lint-ignore: icon sizing in em
          ml={1}
        />
      </Flex>
      {isEmpty ? (
        <Panel>
          <EmptyState icon={FaBookOpen} title="Nothing here yet">
            Content will appear here once it's made available to you.
          </EmptyState>
        </Panel>
      ) : displayMode === "list" ? (
        <Panel>{modules}</Panel>
      ) : (
        <Grid
          gridGap={3}
          gridTemplateColumns={["1fr", "1fr", "1fr 1fr", "1fr 1fr 1fr"]}
        >
          {modules}
        </Grid>
      )}
    </>
  );
}
